import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type AvailableSymbol = {
  exchange: string;
  market: string;
  symbol: string;
  wsSymbol?: string;
  restPair?: string;
  restKey?: string;
};

export type ActiveSymbol = {
  jobKey: string;
  exchange: string;
  market: string;
  symbol: string;
  /** Well-known display form (XRP/USD) carried in the payload itself. */
  wsSymbol?: string | undefined;
  desired: 'running' | 'stopped';
  state: string;
  ownerId?: string | undefined;
  backfillPhase?: string | undefined;
  backfillProgress?: number | undefined;
  backfillMessage?: string | undefined;
  requeueCount?: number | undefined;
  lastError?: string | undefined;
  /**
   * Operational pipeline status from market-data. Empty/undefined for
   * legacy server replies that pre-date the field; the operational
   * status helper treats `undefined` as "no live verification gate to
   * check" so existing callers keep working.
   */
  pipelineStatus?: string | undefined;
  /**
   * MEASURED readiness from the coverage ledger — 'live-verified' means
   * data-integrity measured the recent 24h window complete within the last
   * 10 minutes; trumps phase-flag heuristics. Absent on legacy replies.
   */
  readiness?: 'live-verified' | 'building' | undefined;
  recentCompletenessPct?: number | undefined;
  /**
   * Number of pipeline retry attempts so far. Populated when
   * pipelineStatus === 'integrity-failed'. Optional for back-compat
   * with WS snapshots that pre-date the field.
   */
  retryAttempts?: number | undefined;
  /**
   * Epoch-ms timestamp when the next auto-retry is eligible. Null means
   * no retry is scheduled (operator action required). Optional for
   * back-compat with WS snapshots that pre-date the field.
   */
  retryNextEligibleAt?: number | null | undefined;
  /**
   * Human-readable sub-phase message (e.g. "OHLC 43% < threshold").
   * Populated by market-data during backfill / verification phases.
   */
  subPhaseMessage?: string | null | undefined;
};

export type ServiceCapacity = {
  maxBackfillSlots: number;
  activeBackfillSlots: number;
  activeSymbols: number;
};

type SymbolState = {
  available: AvailableSymbol[];
  active: ActiveSymbol[];
  /** symbol code → wsSymbol display name (e.g. XETHZUSD → ETH/USD) */
  displayNames: Record<string, string>;
  /** Inverse: wsSymbol → symbol code (e.g. ETH/USD → XETHZUSD). Used to
   *  normalize server-pushed WS messages back to the internal code that
   *  selectors/routes compare against. */
  wsToCode: Record<string, string>;
  /** jobKey → previous state (for detecting restarts) */
  previousStates: Record<string, string>;
  /** market-data service capacity info */
  capacity: ServiceCapacity | null;
  setAvailable: (symbols: AvailableSymbol[]) => void;
  setActive: (symbols: ActiveSymbol[], capacity?: ServiceCapacity) => void;
  updateProgress: (
    jobKey: string,
    phase: string,
    progress?: number,
    message?: string,
  ) => void;
  removeActive: (jobKey: string) => void;
};

export const useSymbolStore = create<SymbolState>()(
  persist(
    (set) => ({
      available: [],
      active: [],
      displayNames: {},
      wsToCode: {},
      previousStates: {},
      capacity: null,
      setAvailable: (available) => {
        const displayNames: Record<string, string> = {};
        const wsToCode: Record<string, string> = {};
        for (const s of available) {
          if (s.wsSymbol) {
            displayNames[s.symbol] = s.wsSymbol;
            wsToCode[s.wsSymbol] = s.symbol;
            // Also map BTC↔XBT aliases so either form resolves to the canonical code
            if (s.wsSymbol.startsWith('BTC/')) {
              wsToCode['XBT/' + s.wsSymbol.slice(4)] = s.symbol;
            } else if (s.wsSymbol.startsWith('XBT/')) {
              wsToCode['BTC/' + s.wsSymbol.slice(4)] = s.symbol;
            }
          }
        }
        set({ available, displayNames, wsToCode });
      },
      setActive: (active, capacity) =>
        set((state) => {
          // Build new previousStates: snapshot current state before overwriting
          const activeKeys = new Set(active.map((a) => a.jobKey));
          const prevStates: Record<string, string> = {};
          // Keep only states for symbols still in the active list
          for (const a of state.active) {
            if (activeKeys.has(a.jobKey)) {
              prevStates[a.jobKey] = a.state;
            }
          }
          // Merge display names carried IN the active payload. The dashboard
          // previously depended on the separately-fetched available list for
          // code→wsSymbol mapping; when that fetch lagged or failed, raw
          // Kraken codes (XXRPZUSD) leaked into the UI. The active payload is
          // now self-describing — never render a code we were told a name for.
          let displayNames = state.displayNames;
          let wsToCode = state.wsToCode;
          let mutated = false;
          for (const a of active) {
            if (a.wsSymbol && displayNames[a.symbol] !== a.wsSymbol) {
              if (!mutated) {
                displayNames = { ...displayNames };
                wsToCode = { ...wsToCode };
                mutated = true;
              }
              displayNames[a.symbol] = a.wsSymbol;
              wsToCode[a.wsSymbol] = a.symbol;
            }
          }
          return {
            active,
            previousStates: prevStates,
            ...(mutated ? { displayNames, wsToCode } : {}),
            ...(capacity ? { capacity } : {}),
          };
        }),
      updateProgress: (jobKey, phase, progress, message) =>
        set((state) => ({
          active: state.active.map((a) =>
            a.jobKey === jobKey
              ? { ...a, backfillPhase: phase, backfillProgress: progress, backfillMessage: message }
              : a,
          ),
        })),
      removeActive: (jobKey) =>
        set((state) => ({
          active: state.active.filter((a) => a.jobKey !== jobKey),
        })),
    }),
    {
      name: 'lynx-symbol-names',
      storage: createJSONStorage(() => localStorage),
      // Only persist the symbol→display lookup tables. active/available/
      // capacity/previousStates are fresh every session — rehydrating them
      // would show stale running-state info on load.
      partialize: (s) => ({
        displayNames: s.displayNames,
        wsToCode: s.wsToCode,
      }),
    },
  ),
);

/**
 * Resolve a symbol code to its display name (wsSymbol).
 * Falls back to the raw code if no mapping exists.
 */
export function displaySymbol(
  symbolCode: string,
  displayNames: Record<string, string>,
): string {
  return displayNames[symbolCode] ?? symbolCode;
}

/**
 * Normalize a server-pushed symbol (usually wsSymbol like "ETH/USD") back
 * to the internal REST code (like "XETHZUSD") used as store key by the rest
 * of the app. Falls back to the input unchanged if no mapping exists yet.
 */
export function normalizeServerSymbol(
  symbol: string,
  wsToCode: Record<string, string>,
): string {
  return wsToCode[symbol] ?? symbol;
}

/**
 * Returns true if a symbol was previously 'live' or 'backfilling' but is
 * now in 'starting' or 'idle' — indicating a restart.
 */
export function isRestarting(
  symbol: ActiveSymbol,
  previousStates: Record<string, string>,
): boolean {
  const prev = previousStates[symbol.jobKey];
  if (!prev) return false;
  const wasRunning = prev === 'live' || prev === 'backfilling';
  const isRecovering = symbol.state === 'starting' || symbol.state === 'idle';
  return wasRunning && isRecovering;
}
